import React from "react";
import { styled, TextareaAutosize } from "@mui/material";

interface MessageTextInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyUp?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLTextAreaElement>) => void;
  onSelect?: (e: React.SyntheticEvent<HTMLTextAreaElement>) => void;
  onInput: () => void;
  placeholder?: string;
  isAdmin?: boolean;
  disabled?: boolean;
  gradientFrame?: boolean;
  withResponseContext?: boolean;
}

const StyledTextarea = styled(TextareaAutosize, {
  shouldForwardProp: (prop) =>
    prop !== "isAdmin" &&
    prop !== "gradientFrame" &&
    prop !== "withResponseContext",
})<{
  isAdmin?: boolean;
  gradientFrame?: boolean;
  withResponseContext?: boolean;
}>(
  ({ theme, isAdmin, gradientFrame, withResponseContext }) => ({
    width: "100%",
    resize: "none",
    borderRadius: gradientFrame ? "18px" : "20px",
    overflow: "auto",
    color: gradientFrame
      ? theme.palette.grey[900]
      : theme.palette.footer?.textInput?.color,
    border: gradientFrame
      ? "1px solid transparent"
      : `1px solid ${theme.palette.footer?.textInput?.border}`,
    backgroundColor: gradientFrame
      ? "#FFFFFF"
      : theme.palette.footer?.textInput?.bg,
    fontFamily: theme.typography.fontFamily,
    padding: isAdmin
      ? gradientFrame
        ? withResponseContext
          ? "9px 148px 3px 0.9rem"
          : "9px 44px 3px 0.9rem"
        : "17px 50px"
      : gradientFrame
        ? withResponseContext
          ? "9px 148px 3px 0.9rem"
          : "9px 44px 3px 0.9rem"
        : "17px 50px 17px 3.5rem",
    fontSize: gradientFrame ? "16px" : undefined,
    lineHeight: gradientFrame ? 1.35 : undefined,
    textAlign: "left",
    "&::placeholder": {
      fontSize: gradientFrame ? "inherit" : "12px",
      ...(gradientFrame
        ? {
            color: theme.palette.grey[600],
            opacity: 1,
          }
        : {}),
      [`@media (min-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: gradientFrame ? "inherit" : "16px",
      },
    },
    [theme.breakpoints.down("sm")]: {
      padding: isAdmin
        ? gradientFrame
          ? withResponseContext
            ? "0 118px 0 0.75rem"
            : "0 34px 0 0.75rem"
          : "6px 33px 8px"
        : gradientFrame
          ? withResponseContext
            ? "0 118px 0 0.75rem"
            : "0 36px 0 0.75rem"
          : "6px 33px 8px 3rem",
      ...(gradientFrame
        ? {
            fontSize: "13px",
            lineHeight: "34px",
            "&::placeholder": {
              fontSize: "11px",
              lineHeight: "34px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }
        : {}),
    },
    [`@media (max-width: 360px)`]: {
      ...(gradientFrame
        ? {
            padding: withResponseContext
              ? "0 110px 0 0.65rem"
              : "0 36px 0 0.65rem",
            fontSize: "12px",
            lineHeight: "34px",
            "&::placeholder": {
              fontSize: "10px",
              lineHeight: "34px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "clip",
            },
          }
        : {}),
    },
    ...(gradientFrame
      ? {
          // Prevent autosize growth in footer input (Shift+Enter should scroll).
          height: "34px !important",
          minHeight: "34px !important",
          maxHeight: "34px !important",
          overflowY: "auto",
          [theme.breakpoints.between("sm", "md")]: {
            padding: isAdmin
              ? withResponseContext
                ? "11px 160px 3px 0.9rem"
                : "11px 44px 3px 0.9rem"
              : withResponseContext
                ? "11px 160px 3px 0.9rem"
                : "11px 44px 3px 0.9rem",
            lineHeight: 1.4,
            height: "40px !important",
            minHeight: "40px !important",
            maxHeight: "40px !important",
          },
        }
      : {}),
    "&:focus": {
      outline: "none",
      borderColor: gradientFrame ? "transparent" : theme.palette.primary.main,
    },
  }),
);

export const MessageTextInput = React.forwardRef<
  HTMLTextAreaElement,
  MessageTextInputProps
>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onKeyUp,
      onClick,
      onSelect,
      onInput,
      placeholder = "Your text",
      isAdmin,
      disabled,
      gradientFrame,
      withResponseContext,
    },
    ref,
  ) => {
    const isInputArabic =
      value.trim() && /^[\u0600-\u06FF]/.test(value.trim().split(/\s+/)[0]);

    return (
      <StyledTextarea
        isAdmin={isAdmin}
        gradientFrame={gradientFrame}
        withResponseContext={withResponseContext}
        ref={ref}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onClick={onClick}
        onSelect={onSelect}
        onInput={onInput}
        placeholder={placeholder}
        minRows={1}
        maxRows={gradientFrame ? 1 : 3}
        style={{
          direction: isInputArabic ? "rtl" : "ltr",
        }}
      />
    );
  },
);

MessageTextInput.displayName = "MessageTextInput";
